import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { isAuthenticated, getCurrentUser } from '../services/auth';
import api from '../services/api';
import VerifyEmailModal from '../components/VerifyEmailModal';

const Downloads = () => {
  const { darkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [showAuthMessage, setShowAuthMessage] = useState(false);
  const [downloadingWindows, setDownloadingWindows] = useState(false);
  const [downloadInfo, setDownloadInfo] = useState({ version: null, releaseDate: null, fileSize: null });
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const redirectTimeoutRef = useRef(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  // Fetch latest download info from Azure Blob
  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const response = await api.get('/download/info');
        setDownloadInfo(response.data);
      } catch (err) {
        console.error('Failed to fetch download info:', err);
      }
    };
    fetchInfo();
  }, []);

  const user = getCurrentUser();
  const isVerified = user?.IsEmailVerified === true || user?.isEmailVerified === true;
  
  const handleDownload = async (platform) => {
    if (!isAuthenticated()) {
      setShowAuthMessage(true);
      redirectTimeoutRef.current = setTimeout(() => {
        navigate('/login');
      }, 2000);
    } else {
      // Only Windows is currently available, macOS is "Coming Soon"
      if (platform !== 'windows') {
        return; // macOS not available yet
      }

      try {
        setDownloadingWindows(true);

        // Proceed with download using authenticated API call
        const endpoint = platform === 'windows' ? '/download/windows' : '/download/mac';

        const response = await api.get(endpoint, {
          responseType: 'blob' // Important for file downloads
        });

        // Extract filename from Content-Disposition header if available
        let fileName = platform === 'windows' ? 'TorqueInstaller.exe' : 'TorqueInstaller.dmg';
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1].replace(/['"]/g, '');
          }
        }

        // Create blob URL and trigger download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
        // Show generic error message
        alert('Download failed. Please try again or contact support.');
      } finally {
        setDownloadingWindows(false);
      }
    }
  };

  return (
    <div className={`flex-1 flex flex-col ${darkMode ? 'dark' : ''} bg-gray-100 dark:bg-gray-900`}>
      <div className="px-4 sm:px-6 lg:px-8 flex-grow">
        <div className="max-w-[1440px] mx-auto py-10">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Downloads</h1>

        {/* Authentication Message */}
        {showAuthMessage && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-md">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-sm font-medium">
                An account is required to download Stonewall Invoicing. You'll need a license key to activate the software. Redirecting to login...
              </p>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Stonewall Invoicing</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            Download the latest version of Stonewall Invoicing for your operating system. Our software is designed to help automotive repair shops and service centers manage their business more efficiently.
          </p>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Windows Download */}
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-6 border border-gray-300 dark:border-gray-600">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-blue-600 dark:text-blue-400 mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M0,0H11.377V11.372H0ZM12.623,0H24V11.372H12.623ZM0,12.623H11.377V24H0Zm12.623,0H24V24H12.623" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Windows</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">Compatible with Windows 10 and Windows 11</p>
              <div className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                <div className="flex justify-between">
                  <span>Version:</span>
                  <span>{downloadInfo.version || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Released:</span>
                  <span>{downloadInfo.releaseDate ? new Date(downloadInfo.releaseDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Size:</span>
                  <span>{downloadInfo.fileSize || '—'}</span>
                </div>
              </div>
              <button
                onClick={() => {
                  if (!isAuthenticated()) {
                    handleDownload('windows');
                  } else if (!isVerified) {
                    setShowVerifyModal(true);
                  } else {
                    handleDownload('windows');
                  }
                }}
                disabled={downloadingWindows || !isAuthenticated()}
                className={`w-full py-2 px-4 rounded-md text-sm font-medium flex items-center justify-center transition-colors ${
                  downloadingWindows
                    ? 'bg-gray-500 dark:bg-gray-600 cursor-not-allowed'
                    : !isAuthenticated()
                      ? 'bg-amber-500 dark:bg-amber-600 cursor-not-allowed'
                      : !isVerified
                        ? 'bg-amber-500 hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-700 cursor-pointer'
                        : 'bg-blue-600 hover:bg-blue-700'
                } text-white`}
              >
                {downloadingWindows ? (
                  <>
                    <svg className="w-5 h-5 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Downloading...
                  </>
                ) : !isAuthenticated() ? (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Create Account to Download
                  </>
                ) : !isVerified ? (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Verify Email
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download for Windows
                  </>
                )}
              </button>
            </div>

            {/* macOS Download */}
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-6 border border-gray-300 dark:border-gray-600">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-gray-900 dark:text-white mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">macOS</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">Compatible with macOS 10.14 and later</p>
              <div className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                <div className="flex justify-between">
                  <span>Version:</span>
                  <span>—</span>
                </div>
                <div className="flex justify-between">
                  <span>Released:</span>
                  <span>—</span>
                </div>
                <div className="flex justify-between">
                  <span>Size:</span>
                  <span>—</span>
                </div>
              </div>
              <button
                disabled={true}
                className="w-full py-2 px-4 rounded-md text-sm font-medium flex items-center justify-center bg-gray-500 cursor-not-allowed text-white"
              >
                Coming Soon
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">System Requirements</h2>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Windows Requirements */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Windows</h3>
              <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Windows 10 (64-bit) or Windows 11</li>
                <li>4 GB RAM (8 GB recommended)</li>
                <li>1 GB available disk space</li>
                <li>1280 x 768 screen resolution</li>
                <li>Internet connection for updates and cloud features</li>
              </ul>
            </div>

            {/* macOS Requirements - Commented out for now */}
            {/* <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">macOS</h3>
              <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-2">
                <li>macOS 10.14 (Mojave) or later</li>
                <li>4 GB RAM (8 GB recommended)</li>
                <li>1 GB available disk space</li>
                <li>1280 x 768 screen resolution</li>
                <li>Internet connection for updates and cloud features</li>
              </ul>
            </div> */}
          </div>
        </div>

        <div className="bg-blue-600 dark:bg-blue-700 rounded-lg shadow-md p-6 mb-8 text-white">
          <h2 className="text-xl font-semibold mb-4">Need Help Getting Started?</h2>
          <p className="mb-4">
            Check out our comprehensive documentation to help you get the most out of Stonewall Invoicing.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/documentation" className="inline-flex items-center px-4 py-2 bg-white text-blue-600 rounded-md text-sm font-medium hover:bg-blue-50">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Documentation
            </Link>
          </div>
        </div>

        </div>
      </div>

      <VerifyEmailModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        userEmail={user?.email || user?.Email}
      />
    </div>
  );
};

export default Downloads;